import { BookPlus, Check, Plus, Sparkles, Undo2, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Entity, type ReviewSegment } from "@/lib/api";
import { cn } from "@/lib/cn";
import { entityIcon, entityTypeLabel } from "@/lib/entityTypes";

/**
 * The offer that follows a corrected word: teach the organization this is a word it says.
 *
 * Entities are the things we talk about, aliases are what we call them, and the transcriber
 * is told the ones we say most — so a word taught here is a word the *next* recording comes
 * back with already right, rather than one somebody fixes again. That is the whole argument
 * for putting it on this screen: it is the only place where the person, the word and the
 * knowledge of what it meant are in the same room at the same moment.
 *
 * Two outcomes, and no third:
 *
 *   * **Attach it to something that exists.** "Common" becomes an alias of the BI Common
 *     Dataset, which is the case this feature was built for. The existing entity is
 *     re-fetched rather than patched from the list in memory, because the alias write sends
 *     the whole set and a stale copy would silently drop an alias somebody added elsewhere.
 *   * **Make it a term.** For a word that is nobody's alias — the shorthand a team says with
 *     no thing behind it — with the word itself as the name. Refining it into "Common is our
 *     name for the BI Common Dataset" is Entities' job, and there is a link to go and do it.
 *
 * Collapsed to a single chip until clicked. It appears under a segment somebody has just
 * typed in, at the moment their attention is on that sentence, and an expanded panel there
 * would be a form interrupting a read.
 */
export function Teach({ word, onClose }: { word: string; onClose: () => void }) {
  const { api, entities, refreshEntities } = useApp();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  // An entity already carrying this exact word has nothing to learn from it, so the offer
  // is not made at all — the commonest reason a word was right is that somebody taught it
  // last week.
  const known = useMemo(
    () =>
      entities.some(
        (entity) =>
          entity.name.toLowerCase() === word.toLowerCase()
          || entity.aliases.some((alias) => alias.toLowerCase() === word.toLowerCase()),
      ),
    [entities, word],
  );

  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const pool = needle === "" ? entities : entities.filter(
      (entity) =>
        entity.name.toLowerCase().includes(needle)
        || entity.aliases.some((alias) => alias.toLowerCase().includes(needle)),
    );

    // Most-mentioned first, because the thing a shorthand refers to is nearly always
    // something the organization already talks about a lot.
    return [...pool].sort((a, b) => b.mentionCount - a.mentionCount).slice(0, 5);
  }, [entities, query]);

  const attach = async (entity: Entity) => {
    if (busy) return;
    setBusy(true);
    setProblem(null);

    try {
      const fresh = await api.getEntity(entity.id);
      await api.setEntityAliases(entity.id, [...fresh.entity.aliases, word]);
      refreshEntities();
      setDone(`“${word}” is now one of the names for ${entity.name}.`);
    } catch (cause: unknown) {
      // The Server's 409 already reads as a sentence naming what holds the word, so it is
      // shown as it stands rather than rewritten into something vaguer.
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const asTerm = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);

    try {
      await api.createEntity({ type: "term", name: word, description: "" });
      refreshEntities();
      setDone(`“${word}” is now a term. The transcriber will listen for it.`);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  if (known) return null;

  if (done !== null) {
    return (
      <p
        role="status"
        className="mt-[var(--space-3)] flex items-center gap-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-secondary)]"
      >
        <Check size={13} strokeWidth={2.5} className="shrink-0 text-[var(--vl-life-soft)]" />
        {done}
        <Link to="/entities" className="text-[var(--vl-bridge)] underline-offset-2 hover:underline">
          Entities
        </Link>
      </p>
    );
  }

  if (!open) {
    return (
      <div className="mt-[var(--space-3)] flex items-center gap-[var(--space-2)]">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--border-subtle)] px-[var(--space-3)] py-1 text-[length:var(--text-xs)] text-[var(--text-secondary)] transition-colors hover:border-[var(--border-default)] hover:text-[var(--text-primary)]"
        >
          <BookPlus size={13} strokeWidth={2} />
          Listen for “{word}” next time
        </button>

        <button
          type="button"
          onClick={onClose}
          aria-label={`Dismiss the suggestion to remember “${word}”`}
          className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
        >
          <X size={12} strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-2)] p-[var(--space-3)]">
      <div className="flex items-baseline justify-between gap-[var(--space-3)]">
        <p className="text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)]">
          What is “{word}”?
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
        >
          <X size={13} strokeWidth={2} />
        </button>
      </div>

      <input
        ref={field}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => event.key === "Escape" && (event.stopPropagation(), onClose())}
        placeholder="Search what you already know about…"
        aria-label={`What “${word}” refers to`}
        className="mt-[var(--space-2)] h-8 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-1)] px-2 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
      />

      <div className="mt-[var(--space-2)] flex flex-col gap-0.5">
        {suggestions.map((entity) => {
          const Icon = entityIcon(entity.type);

          return (
            <button
              key={entity.id}
              type="button"
              disabled={busy}
              onClick={() => void attach(entity)}
              className="flex items-center gap-[var(--space-2)] rounded-[var(--radius-md)] px-2 py-1.5 text-left text-[length:var(--text-xs)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-glass-md)] hover:text-[var(--text-primary)] disabled:opacity-50"
            >
              <Icon size={13} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />
              <span className="flex-1 truncate">{entity.name}</span>
              <span className="shrink-0 text-[var(--text-muted)]">{entityTypeLabel(entity.type)}</span>
            </button>
          );
        })}

        <button
          type="button"
          disabled={busy}
          onClick={() => void asTerm()}
          className="flex items-center gap-[var(--space-2)] rounded-[var(--radius-md)] px-2 py-1.5 text-left text-[length:var(--text-xs)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-glass-md)] hover:text-[var(--text-primary)] disabled:opacity-50"
        >
          <Plus size={13} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />
          <span className="flex-1 truncate">
            A word we use — add “{word}” as a term
          </span>
        </button>
      </div>

      {problem && (
        <p role="alert" className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ proposals (SPCH-8)

/**
 * The Reasoner's entity-aware corrections for one segment, and the owner's toggle over
 * each (SPCH-8). A pending one is the `Unverified` change — strikethrough before, the
 * correction after, the reasoning in muted italics — with Approve and Discard. A decided
 * one shows what it is and offers the way back, because the whole affordance is a toggle
 * and a second thought is not an error.
 */
export function Proposals({
  proposals,
  saving,
  onResolve,
}: {
  proposals: ReviewSegment["proposals"];
  saving: boolean;
  onResolve: (correctionId: string, apply: boolean) => Promise<unknown>;
}) {
  return (
    <div className="mt-2.5 flex flex-col gap-2">
      {proposals.map((proposal) => (
        <div
          key={proposal.id}
          className="rounded-[var(--radius-lg)] border border-[color-mix(in_oklch,var(--vl-accent-warm)_28%,transparent)] bg-[color-mix(in_oklch,var(--vl-accent-warm)_5%,transparent)] p-[var(--space-3)]"
        >
          <div className="mb-1.5 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--vl-accent-warm)]">
              <Sparkles size={11} strokeWidth={2} />
              {proposal.category ?? "Suggested correction"}
            </span>
            {proposal.status === "applied" && (
              <span className="text-[length:var(--text-2xs)] text-[var(--vl-life-soft)]">Applied</span>
            )}
            {proposal.status === "discarded" && (
              <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">Discarded</span>
            )}
          </div>

          <p className="text-[length:var(--text-sm)] leading-[1.6]">
            <span className="text-[var(--text-muted)] line-through decoration-[color-mix(in_oklch,var(--warning)_60%,transparent)]">
              {proposal.originalText}
            </span>{" "}
            <span aria-hidden className="text-[var(--text-muted)]">→</span>{" "}
            <span
              className={cn(
                "text-[var(--vl-life-soft)]",
                proposal.status === "discarded" && "line-through opacity-60",
              )}
            >
              {proposal.correctedText}
            </span>
          </p>

          {proposal.reasoning && (
            <p className="mt-1 text-[length:var(--text-xs)] italic text-[var(--text-muted)]">{proposal.reasoning}</p>
          )}

          <div className="mt-2 flex items-center gap-1.5">
            {proposal.status !== "applied" && (
              <button
                type="button"
                disabled={saving}
                onClick={() => void onResolve(proposal.id, true)}
                className="inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-2.5 text-[length:var(--text-xs)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
              >
                <Check size={13} strokeWidth={2.5} /> Approve
              </button>
            )}
            {proposal.status !== "discarded" && (
              <button
                type="button"
                disabled={saving}
                onClick={() => void onResolve(proposal.id, false)}
                className="inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-2.5 text-[length:var(--text-xs)] text-[var(--text-secondary)] transition hover:bg-[var(--surface-glass)] disabled:opacity-50"
              >
                {proposal.status === "applied" ? <Undo2 size={13} strokeWidth={2} /> : <X size={13} strokeWidth={2} />}
                {proposal.status === "applied" ? "Undo" : "Discard"}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ the editor

export function Editor({
  segment,
  saving,
  select,
  onClose,
  onSave,
  onRevert,
}: {
  segment: ReviewSegment;
  saving: boolean;
  /** Characters to have selected when the field opens, for the word that was clicked. */
  select: [number, number] | null;
  onClose: () => void;
  onSave: (text: string) => void;
  onRevert: () => Promise<unknown>;
}) {
  const [draft, setDraft] = useState(segment.text);
  const field = useRef<HTMLTextAreaElement>(null);

  // What the Server has been told, so that a sentence saved on the way out and then
  // saved again with Enter is one correction rather than two.
  const sent = useRef(segment.text.trim());

  const commit = (text: string) => {
    const next = text.trim();

    // A segment emptied of its last word would leave the Server nothing to store, and
    // deleting a segment is not something this screen does. So that one is left alone.
    if (next === "" || next === sent.current) return;

    sent.current = next;
    onSave(next);
  };

  useEffect(() => {
    const node = field.current;
    if (!node) return;

    node.focus();

    // Selected, not just focused: clicking a word means "this one is wrong", so the
    // first letter typed should replace it. Everything around it stays right there to
    // be arrowed into, clicked into or typed onto — which is the difference between
    // correcting a word and being trapped in a box around one.
    if (select) node.setSelectionRange(select[0], select[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exactly as tall as what it holds, and no taller. A fixed three rows is a box around
  // a one-line segment and a scrollbar around a five-line one; either way the sentence
  // stops sitting where it sat a moment ago, which is the thing that makes an editor
  // feel like somewhere else rather than the same text with a caret in it.
  useLayoutEffect(() => {
    const node = field.current;
    if (!node) return;

    node.style.height = "auto";
    // jsdom measures nothing, and a height of zero there would hide the field from the
    // tests that type into it.
    if (node.scrollHeight > 0) node.style.height = `${node.scrollHeight}px`;
  }, [draft]);

  /**
   * Closes, but only once the press that asked for it has finished.
   *
   * This is the whole reason clicking from one sentence into the next lands where it was
   * aimed. A mouse press blurs this field, and closing here and then would take these
   * buttons out of the page *between the press and the click it belongs to* — moving
   * every sentence below up by their height, so the click would open the word a line
   * above the one under the pointer. Waiting for the release costs nothing and keeps the
   * page still while a click is in the air: the click is still delivered to the word it
   * started on, however the page has moved since.
   */
  const pending = useRef<(() => void) | null>(null);

  const closeAfterPress = () => {
    if (pending.current) return;

    const run = () => {
      pending.current = null;
      onClose();
    };

    pending.current = run;
    document.addEventListener("mouseup", run, { once: true });
  };

  useEffect(
    () => () => {
      if (pending.current) document.removeEventListener("mouseup", pending.current);
    },
    [],
  );

  return (
    <div className="flex flex-col gap-2">
      <textarea
        ref={field}
        value={draft}
        rows={1}
        aria-label="Corrected text"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
          } else if (event.key === "Enter" && !event.shiftKey) {
            // A segment is one turn of speech, so Enter is far likelier to mean "done"
            // than "new line" — and a correction that needs a line break can still say
            // so with shift.
            event.preventDefault();
            commit(draft);
            onClose();
          }
        }}
        // Clicking away keeps what was typed, including clicking straight into the next
        // sentence. Losing an edit because the pointer moved on is the thing that stops
        // people making small corrections at all.
        onBlur={() => {
          commit(draft);
          closeAfterPress();
        }}
        // The reading type, to the letter — 19px, the same leading, the same tracking —
        // with padding that an equal negative margin gives back, and an outline where a
        // border would be. Nothing about the sentence moves when it opens.
        className="-mx-1 w-[calc(100%+0.5rem)] resize-none overflow-hidden rounded-[var(--radius-md)] bg-[color-mix(in_oklch,var(--surface-2)_70%,transparent)] px-1 py-0 text-[19px] leading-[1.85] tracking-[-0.005em] text-[var(--text-primary)] outline outline-1 outline-[var(--vl-bridge)] [text-wrap:pretty]"
      />

      {/* Neither button may take the focus, or pressing one would blur the field and
          close the editor out from under the click on its way. */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={saving || draft.trim().length === 0}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            commit(draft);
            onClose();
          }}
          className="h-7 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          Save correction
        </button>

        {/* One button, two meanings, exactly as the handoff draws it: cancel a draft
            that was never saved, and take back a correction that was. */}
        <button
          type="button"
          disabled={saving}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => (segment.correction ? void onRevert() : onClose())}
          className="h-7 rounded-[var(--radius-md)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] transition hover:bg-[var(--surface-glass)] hover:text-[var(--text-primary)] disabled:opacity-50"
        >
          Revert
        </button>

        <span className="ml-auto text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          Word timings redistribute across the segment
        </span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the player bar
